import { useEffect, useState } from 'react'
import { fetchKerncijfers } from '../helpers/cbs/fetchCbs'
import { fetchBuildingFacts } from '../helpers/bag/fetchBag'
import { fetchEnergyLabel } from '../helpers/energylabel/fetchEnergyLabel'

// Fetches open external data for a single address on demand — nothing is stored.
// Drives the read-only external-data table on the gegevens tab:
//   - CBS "Kerncijfers wijken en buurten" for the address's BuurtCode
//   - BAG building facts for the address's BAGid (verblijfsobject -> pand)
//   - EP-Online energy label for the address's BAGid (via Cloud Function proxy)
// The sources are independent: one failing does not blank the others.
// energyLabelStatus distinguishes "no label registered" (ready + null) from
// "lookup failed" (error) so the card can say which one it is.
//
// AI-CONTEXT: See docs/features/adress-external-data.md
const useAdressExternalData = (location) => {
    const buurtCode = location?.BuurtCode || ''
    const bagId = location?.BAGid || ''

    const [cbs, setCbs] = useState(null)
    const [bag, setBag] = useState({ vob: null, pand: null })
    const [energyLabel, setEnergyLabel] = useState(null)
    const [energyLabelStatus, setEnergyLabelStatus] = useState('idle')
    const [status, setStatus] = useState('idle')

    useEffect(() => {
        if (!buurtCode && !bagId) {
            setStatus('idle')
            return
        }

        const controller = new AbortController()
        setStatus('loading')
        // The energy label settles independently: the callable can be seconds
        // slower (cold start) than the keyless CBS/BAG fetches, and those
        // results should not wait for it. Reset the label so a re-run for a
        // different address never shows the previous address's label.
        setEnergyLabel(null)
        setEnergyLabelStatus(bagId ? 'loading' : 'idle')

        Promise.allSettled([
            buurtCode ? fetchKerncijfers(buurtCode, controller.signal) : Promise.resolve(null),
            bagId ? fetchBuildingFacts(bagId, controller.signal) : Promise.resolve({ vob: null, pand: null }),
        ]).then(([cbsResult, bagResult]) => {
            if (controller.signal.aborted) return

            if (cbsResult.status === 'fulfilled') setCbs(cbsResult.value)
            else console.error('CBS kerncijfers fetch failed', cbsResult.reason)

            if (bagResult.status === 'fulfilled') setBag(bagResult.value)
            else console.error('BAG building facts fetch failed', bagResult.reason)

            const bothFailed = cbsResult.status === 'rejected' && bagResult.status === 'rejected'
            setStatus(bothFailed ? 'error' : 'ready')
        })

        if (bagId) {
            fetchEnergyLabel(bagId)
                .then((label) => {
                    if (controller.signal.aborted) return
                    setEnergyLabel(label)
                    setEnergyLabelStatus('ready')
                })
                .catch((error) => {
                    if (controller.signal.aborted) return
                    console.error('EP-Online energy label fetch failed', error)
                    setEnergyLabelStatus('error')
                })
        }

        return () => controller.abort()
    }, [buurtCode, bagId])

    return { cbs, bag, energyLabel, energyLabelStatus, status }
}

export default useAdressExternalData
